import { Paper, Typography, useTheme } from '@mui/material';
import type { DefaultizedPieValueType } from '@mui/x-charts/models';
import { PieChart } from '@mui/x-charts/PieChart';
import type { GetUsageByMeterResponse } from '@weft/api-definition';
import { isNotEmptyArray, isNotNullOrUndefined, percentage, sumBy } from '@weft/utils';
import { PieChartHoverWrapper } from 'apps/app/src/components/new-design/charts/pie-chart/PieChartHoverWrapper';
import { formatPercentage } from 'apps/app/src/utils/number';
import { useState } from 'react';
import { UsageByMeterChartCenterLabel } from './usage-by-meter-chart-center-label';
import { getUsageByMeterBaseColorByIndex } from './usage-by-meter-section-utils/get-usage-by-meter-base-color-by-index';

const INDEX_NOT_FOUND = -1;

type UsageMyMeterChartProps = {
  usageByMeter: GetUsageByMeterResponse['usageByMeter']
};

export const UsageMyMeterChart: React.FC<UsageMyMeterChartProps> = ({ usageByMeter }) => {
  const vyTheme = useTheme();
  const [lastHoveredIndex, setLastHoveredIndex] = useState<number | null>(null);
  const totalUsage = sumBy(usageByMeter, 'usageAmount');
  
  const handleChartHover = (itemData: DefaultizedPieValueType) => {
    const index = usageByMeter.findIndex(({ meterId }) => meterId === itemData.id);

    if (index !== INDEX_NOT_FOUND) {
      setLastHoveredIndex(index);
    }
  };

  return (
    <div className='relative h-[240px] w-[240px]'>
      {isNotEmptyArray(usageByMeter) && <UsageByMeterChartCenterLabel value={totalUsage}/>}
      <PieChart
        width={240}
        height={240}
        slotProps={{ legend: { hidden: true } }}
        tooltip={{
          trigger: 'item',
          itemContent: (props) => (
            <PieChartHoverWrapper
              onChartHover={handleChartHover}
              pieChartHoverProps={props}
              render={(itemData) => (
                <Paper sx={{
                  backgroundColor: '#0D1B27DE',
                  padding: 1,
                  transform: 'translate(-50%, -120%)',
                }}
                >
                  <Typography
                    fontSize="14px"
                    lineHeight="18px"
                    fontWeight={500}
                    textAlign="center"
                    color="white"
                  >
                    {`${itemData.label} ${formatPercentage(percentage(itemData.value, totalUsage))}`}
                  </Typography>
                </Paper>
              )}
            />
          ),
        }}
        series={[
          {
            highlightScope: isNotNullOrUndefined(lastHoveredIndex) ? { faded: 'none', highlighted: 'item' } : undefined,
            highlighted: isNotNullOrUndefined(lastHoveredIndex) ? { color: getUsageByMeterBaseColorByIndex(lastHoveredIndex, vyTheme).dark } : undefined,
            data: usageByMeter.map(({ meterId, usageAmount, meterName }, index) => ({
              id: meterId,
              value: usageAmount,
              label: meterName,
              color: getUsageByMeterBaseColorByIndex(index, vyTheme).light,
            })),
            innerRadius: 90,
            outerRadius: 120,
            paddingAngle: 0.3,
            cornerRadius: 0,
            startAngle: 0,
            endAngle: 360,
            cx: 115,
          },
        ]}
      />
    </div>
  );
};